import moment, {Moment} from 'moment';
import {ColumnType} from 'priority-web-sdk/enums';
import {getUserBLFromServer, isBlacklisted} from '../service/searchesBlacklistService';
import {getLocalizedTextServ} from '../../../_shared/GWTBridge/invoke/server';
import {getUsername} from '../../../_shared/GWTBridge/invoke/genPage';
import {getLanguage} from '../../../_shared/GWTBridge/invoke/common';
import filtersTexts from '../../../_legacy/Filters/texts';
import {
    Operator,
    OperatorMapping,
    OperatorIconMapping
} from '../../../_legacy/Searches/consts/operators';
import {deserialize} from '../../../_legacy/Searches/serializes/filters';
import {dateUS14} from '../../../_legacy/Searches/serializes/filters/date';
import {timeUS_5} from '../../../_legacy/Searches/serializes/filters/range';
import {Form} from '../../../_legacy/Form/stores/Form';
import {
    Entity,
    SearchFilter,
    IndexedSearch
} from '../../../services/indexDB/indexeddbServiceSearches';
import {
    isCurrentNetformCollectingUserActions,
    getIsActionSource,
    getZoomSourceName,
    getTargetZoomName
} from '../../../_shared/GWTBridge/invoke/netForm';
import {INFO_MAP, Templates} from '../../MoreInfo/constants';
import {formsStore} from '../../../_legacy/Form/stores/FormsStore';
import {
    addScreenEntry,
    Entity as moreInfoEntity
} from '../../../services/indexDB/indexeddbServiceZoomedScreens';
import {getUserBLFromServer as getUserMoreInfoServerBL} from '../../Personalization/service/moreInfoBlacklistService';
import {getInfoMap} from '../../MoreInfo/utils/utils';
import {SuggestionType, SnoozedSuggestion} from '../types/personalizationCenter';
import {HOMEPAGE_REFERENCE} from '../consts';
import {getCompanyName} from '../../../utils/proto-api-utils/utils';

export enum PersonalizationType {
    None,
    SubForms,
    Forms,
    Actions,
    Homepage
}

export type FilterTextValues = {
    operatorText: string | null;
    values: string | string[];
    field: string;
    type: string;
};

type minifiedJson = {
    hasAttachment: boolean;
    isPainted: boolean;
    name: string;
    table: string;
    title: string;
    level: string;
};

export const configuredDate = (): Moment => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('_dateOverride')) {
        return moment(urlParams.get('_dateOverride'), 'DD/MM/YYYY');
    }
    return moment();
};

export const today = configuredDate().format('DD/MM/YYYY');

export const calcDaysDiff = (timeObj: Moment) => configuredDate().diff(timeObj, 'days');

export const allowPersonalization = (): boolean => {
    return true;
};

export const getBasicUserData = () => {
    return {
        userName: getUsername(),
        lang: getLanguage(),
        company: getCompanyName()
    };
};

export const getDeserializedFilters = (filtersMap, form: Form) => {
    const deserializedFilters = [];
    filtersMap.forEach(filter => {
        const deserialized = deserialize(filter, form, true);
        let {column, operator} = filter;
        if (operator && typeof operator !== 'number') {
            operator = Array.from(operator)[0];
        }
        const additionalInfo = {
            fieldName: column.name,
            valType: column.type,
            operator:
                column.type !== 'bool'
                    ? operator
                    : deserialized.op === '='
                    ? Operator.Equals
                    : Operator.NotEquals
        };
        deserializedFilters.push({...deserialized, ...additionalInfo});
    });
    return deserializedFilters;
};

const isIdenticalFilter = (filterObj1, filterObj2) => {
    if (filterObj1.fieldName !== filterObj2.fieldName) return false;
    if (filterObj1.isdesc !== filterObj2.isdesc) return false;
    if (filterObj1.operator !== filterObj2.operator) return false;
    if (filterObj1.sort !== filterObj2.sort) return false;
    if (filterObj1.values?.join() !== filterObj2.values?.join()) return false;
    if (filterObj1.fromval) {
        if (filterObj1.fromval !== filterObj2.fromval) return false;
    }
    if (filterObj1.toval) {
        if (filterObj1.toval !== filterObj2.toval) return false;
    }
    if (filterObj1.op) {
        if (filterObj1.op !== filterObj2.op) return false;
    }
    if (filterObj1.value) {
        if (filterObj1.value.min !== filterObj2.value.min) return false;
        if (filterObj1.value.max !== filterObj2.value.max) return false;
    }

    return true;
};

export const isIdenticalSearch = (filtersArr1, filtersArr2) => {
    // filterArr: [{ fieldNum, fieldName, sort, isdesc, operaotor, values: [] }]
    for (let i = 0; i < filtersArr1.length; i++) {
        const identicalFilter = isIdenticalFilter(filtersArr1[i], filtersArr2[i]);
        if (!identicalFilter) return false;
    }
    return true;
};

export const findIndexedSearch = (entity: Entity, searchFilters: SearchFilter[]) => {
    const index = searchFilters.length;
    const relevantSearches = entity.searches[index];

    if (!relevantSearches) {
        return null;
    } else {
        const identicalSearch = relevantSearch => {
            return isIdenticalSearch(searchFilters, relevantSearch.filters);
        };
        return relevantSearches.find(identicalSearch);
    }
};

export const encodeSearchFilters = (filters: SearchFilter[]) => {
    return filters.map(filter => {
        if (filter.valType === 'text') {
            const encodedValues = filter.values.map(val => {
                return btoa(encodeURIComponent(val));
            });
            return {...filter, values: encodedValues};
        } else {
            return filter;
        }
    });
};

export const isPopularSearch = async (
    entity: Entity,
    search: IndexedSearch,
    configuration,
    formName
) => {
    // Verify search is not blacklisted
    const userBlacklist = await getUserBLFromServer(formName);
    const currentFiltersAmount = search.filters.length;
    const relevantBlacklist = userBlacklist && userBlacklist.searches[currentFiltersAmount];
    const blacklisted = !relevantBlacklist
        ? false
        : isBlacklisted(relevantBlacklist, encodeSearchFilters(search.filters));

    if (blacklisted) {
        return false;
    } else {
        // Check if suggestable:
        const userFormSearches = [];
        const entitySearches = entity.searches;
        for (const key in entitySearches) {
            userFormSearches.push(...entitySearches[key]);
        }
        const searchesAboveThreshold = userFormSearches.filter(
            _search => _search.count >= configuration.minSearches
        );

        const currentSearchValue = search.count * currentFiltersAmount;

        // Isolate searches with the higher value or those with equal value and greater number of filters
        const relevantSearches = searchesAboveThreshold.filter(({count, filters}) => {
            const searchValue = count * filters.length;
            if (searchValue > currentSearchValue) {
                return true;
            } else if (
                searchValue === currentSearchValue &&
                filters.length > currentFiltersAmount
            ) {
                return true;
            }
            return false;
        });

        let allBlacklisted = relevantSearches.length ? false : true;
        if (allBlacklisted) return true;
        if (userBlacklist) {
            allBlacklisted = relevantSearches.every(dbSearch => {
                const relevantBlacklist = userBlacklist.searches[dbSearch.filters.length];
                if (!relevantBlacklist) {
                    return false;
                } else {
                    return isBlacklisted(relevantBlacklist, encodeSearchFilters(dbSearch.filters));
                }
            });
        }
        return allBlacklisted;
    }
};

const convertDateToUSDate14 = date => {
    return moment(date, ['MM/DD/YY HH:mm']).format(dateUS14);
};

const convertTimeToUSTime5 = time => {
    return moment(time, ['HH:mm']).format(timeUS_5);
};

const convertValueToUSString = (value, type) => {
    return type === 'date' ? convertDateToUSDate14(value) : convertTimeToUSTime5(value);
};

const convertValuesByType = (filter, format) => {
    switch (filter.valType) {
        case 'text':
            return filter.values;
        case 'bool':
            if (filter.op === OperatorMapping[Operator.Equals]) {
                return filtersTexts.yes;
            } else if (filter.op === OperatorMapping[Operator.NotEquals]) {
                return filtersTexts.no;
            } else {
                return '';
            }
        default:
            const needTranslation =
                getLanguage() === 3 && (format === dateUS14 || format === timeUS_5);
            const from =
                filter.fromval &&
                (!needTranslation
                    ? filter.fromval
                    : convertValueToUSString(filter.fromval, filter.valType));
            const to =
                filter.toval &&
                (!needTranslation
                    ? filter.toval
                    : convertValueToUSString(filter.toval, filter.valType));
            const fromTo = from && to;
            return fromTo ? `${from} - ${to}` : from ? from : to ? to : '';
    }
};

export const convertFilterValsToText = (form, filter): FilterTextValues => {
    const column = form.getColumnByName(filter.fieldName);
    const operatorKey = OperatorIconMapping[filter.operator]?.name;
    const operatorText = operatorKey ? getLocalizedTextServ(operatorKey) : null;
    const values = convertValuesByType(filter, column.format);

    return {
        operatorText,
        values,
        field: column.title,
        type: filter.valType
    };
};

const isMoreInfoField = zoomFname => {
    return Boolean(INFO_MAP[zoomFname]);
};

export const getMostUsedPostZoomScreen = async (entity: moreInfoEntity, zoomFname: string) => {
    const userBlacklist = await getUserMoreInfoServerBL();
    const userInfoMap = await getInfoMap();
    const infoMapEntitys = userInfoMap[zoomFname].map(screen => screen.entityId);
    const blacklistedEntitys = userBlacklist && userBlacklist[zoomFname];
    const excludedEntitys = blacklistedEntitys
        ? [...infoMapEntitys, ...blacklistedEntitys]
        : infoMapEntitys;
    const mostUsed = entity.screens
        ?.filter(screen => !excludedEntitys?.includes(screen.form.entityId))
        .sort((a, b) => b.count - a.count)[0];
    return mostUsed;
};

export const handleMoreInfoPersonalization = (form: minifiedJson) => {
    const isZoomedForm = isCurrentNetformCollectingUserActions();
    const currForm = formsStore.currentForm;
    const sameForm = currForm?.name === form?.name;

    if (isZoomedForm && currForm && !currForm.isHTML && sameForm) {
        const isSubForm = form.level === '1';
        const isDirectAct = getIsActionSource();
        if (isSubForm || isDirectAct) {
            let zoomFname;
            if (isSubForm) {
                const {parentForm} = currForm;
                zoomFname = parentForm?.name;
            } else {
                if (currForm.table !== '1') {
                    // Do not count direct actions which originates form sub froms
                    return;
                }
                zoomFname = getZoomSourceName();
            }
            const isDataCollected = zoomFname && isMoreInfoField(zoomFname);

            const formData = isDataCollected && {
                entityId: form.name,
                formName: isSubForm ? formsStore.currentForm.parentForm.name : form.name,
                subFormName: isSubForm ? form.name : '',
                formTitle: form.title,
                hasAttachment: form.hasAttachment,
                template: form.isPainted ? Templates.column : Templates.list,
                targetColName: getTargetZoomName()
            };

            formData && addScreenEntry({formData, zoomFname});
        }
    }
};

export const extractDataByPersonalizationType = (
    personalizationType: PersonalizationType,
    screen: string
): SnoozedSuggestion | null => {
    let type;
    switch (personalizationType) {
        case PersonalizationType.Homepage:
            type = SuggestionType.Homepage;
            break;
        case PersonalizationType.SubForms:
            type = SuggestionType.Subforms;
            break;
        case PersonalizationType.Actions:
            type = SuggestionType.Actions;
            break;
        default:
            return null;
    }
    return {type, reference: screen || HOMEPAGE_REFERENCE};
};

export const sortCollectionDatesAsc = collectionDatesArray => {
    return collectionDatesArray.sort((a, b) => {
        const date1 = a?.date?.split('/').reverse().join('');
        const date2 = b?.date?.split('/').reverse().join('');
        return date1 > date2 ? 1 : date1 < date2 ? -1 : 0;
    });
};

export const getSearchFilters = (form: Form, searchFilters: SearchFilter[]) => {
    return searchFilters.map((filter, index) => {
        const column = form.getColumnByNumber(filter.field.toString());
        const basicfilterData = {
            column,
            field: filter.field,
            isdesc: filter.isdesc === '1',
            operator: filter.operator,
            sortIndex: index
        };
        switch (column.type) {
            case ColumnType.Bool:
                return {...basicfilterData, value: filter.fromval === 'Y'};
            case ColumnType.Date:
            case ColumnType.Time:
            case ColumnType.Duration:
            case ColumnType.Number:
                return {...basicfilterData, value: {min: filter.fromval, max: filter.toval}};
            default:
                return {...basicfilterData, values: filter.values};
        }
    });
};
